import {useCallback,useEffect,useMemo,useRef,useState} from "react";
import {createPortal} from "react-dom";
import {Sparkles,X} from "lucide-react";
import {api} from "./api";
import {newsDayLabel,recentNews,type NewsItem,type NewsViewer} from "./news";

/** Segundos que a janela fica aberta antes de poder fechar (só quando abre sozinha). */
export const NEWS_READ_DELAY=5;

/**
 * Novidades do SGQ, no mesmo modelo do Portal de Chamados: ao abrir o sistema, mostra o que mudou
 * nos últimos dias e a pessoa ainda não viu; "Entendi" marca como visto na conta. Aberta sozinha,
 * só fecha depois de NEWS_READ_DELAY segundos (para dar tempo de ler); pelo botão ✨ do topo,
 * mostra a semana toda e fecha na hora.
 */
export function WhatsNew({viewer}:{viewer:NewsViewer}){
  const [open,setOpen]=useState(false),[showing,setShowing]=useState<NewsItem[]>([]),[onlyUnseen,setOnlyUnseen]=useState(false),[remaining,setRemaining]=useState(0);
  const locked=open&&remaining>0;
  const checking=useRef(false);
  // A sessão pode trazer um objeto novo a cada render; o que importa é perfil e permissões.
  const viewerKey=`${viewer.role}|${viewer.permissions.join(",")}`;
  const viewerRef=useRef(viewer);
  viewerRef.current=viewer;
  const stableViewer=useMemo(()=>viewerRef.current,[viewerKey]);

  /** Abre sozinho com as novidades ainda não vistas (sem nada novo, não abre). */
  const check=useCallback(async()=>{
    if(checking.current)return;
    const recent=recentNews(stableViewer);
    if(!recent.length)return;
    checking.current=true;
    try{
      const {seen}=await api.seenNews(recent.map(item=>item.id));
      const fresh=recent.filter(item=>!seen.includes(item.id));
      if(fresh.length){setShowing(fresh);setOnlyUnseen(true);setRemaining(NEWS_READ_DELAY);setOpen(true)}
    }catch{
      // Sem saber o que já foi visto, melhor não insistir com a janela.
    }finally{checking.current=false}
  },[stableViewer]);

  // Ao entrar no SGQ e ao voltar para a aba (pode ter saído novidade enquanto estava aberto).
  useEffect(()=>{
    void check();
    const onVisible=()=>{if(document.visibilityState==="visible")void check()};
    document.addEventListener("visibilitychange",onVisible);
    return()=>document.removeEventListener("visibilitychange",onVisible);
  },[check]);

  useEffect(()=>{
    if(!open||remaining<=0)return;
    const timer=window.setTimeout(()=>setRemaining(value=>value-1),1000);
    return()=>window.clearTimeout(timer);
  },[open,remaining]);

  const close=useCallback(()=>{
    if(locked)return;
    setOpen(false);
    // Marca na conta: em outro computador não aparece de novo.
    if(onlyUnseen)void api.markNewsSeen(showing.map(item=>item.id)).catch(()=>undefined);
  },[locked,onlyUnseen,showing]);

  useEffect(()=>{
    if(!open)return;
    const onKey=(event:KeyboardEvent)=>{if(event.key==="Escape")close()};
    document.addEventListener("keydown",onKey);
    return()=>document.removeEventListener("keydown",onKey);
  },[open,close]);

  const reopen=()=>{setShowing(recentNews(stableViewer));setOnlyUnseen(false);setRemaining(0);setOpen(true)};

  const byDay=useMemo(()=>{
    const groups=new Map<string,NewsItem[]>();
    for(const item of showing){const day=newsDayLabel(item.at);groups.set(day,[...(groups.get(day)||[]),item])}
    return [...groups.entries()];
  },[showing]);

  return <>
    <button type="button" className="news-trigger" title="Novidades do SGQ" aria-label="Novidades do SGQ" onClick={reopen}><Sparkles/></button>
    {/* Portal: o topo cria seu próprio contexto de empilhamento e prenderia a janela dentro dele. */}
    {open&&createPortal(<div className="dialog-backdrop news-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)close()}}>
      <section className="news-dialog" role="dialog" aria-modal="true" aria-labelledby="news-title" aria-describedby="news-description">
        <header>
          <span className="news-icon" aria-hidden="true"><Sparkles/></span>
          <div>
            <h2 id="news-title">{onlyUnseen?"Novidades no SGQ":"Novidades da semana"}</h2>
            <p id="news-description">{onlyUnseen?"O que mudou desde a sua última visita.":"O que mudou no SGQ nos últimos dias."}</p>
          </div>
          {!locked&&<button type="button" className="news-close" aria-label="Fechar" onClick={close}><X/></button>}
        </header>
        {showing.length===0?<p className="news-empty">Nenhuma novidade nos últimos dias.</p>:<div className="news-list">
          {byDay.map(([day,items])=><section key={day} aria-label={day}>
            <h3>{day}</h3>
            <ul>{items.map(item=><li key={item.id}><strong>{item.title}</strong><p>{item.text}</p></li>)}</ul>
          </section>)}
        </div>}
        <footer><button type="button" className="button primary" autoFocus disabled={locked} aria-live="polite" onClick={close}>{locked?`Entendi (${remaining})`:"Entendi"}</button></footer>
      </section>
    </div>,document.body)}
  </>;
}
